// ─────────────────────────────────────────────────────────────────────────────
// WP-08 / C2 — Reportería de Reconocimiento (front). SIETE pestañas, las de la maqueta y en su
// orden: Reco Diario · Brillamos Juntos · Históricos · Demográfico · Campañas General · Líderes ·
// Moderación. Cinco salen de v1/analytics/recognition/*; Moderación vive en RecognitionService
// (v1/moderation/report) y Campañas General es propia (RF-151, sin filtros superiores).
//
// Eran NUEVE: `Heatmap` y `Red ONA` tenían pestaña propia y la maqueta no las lista. Su contenido
// NO se perdió — en la maqueta vive dentro del panel Demográfico, y aquí también.
// Reutiliza el shell de tabs y el FilterBar del AnalyticsView (rango + OU + fundamentales) y el
// export Excel del módulo. SIN puntos (regla de oro). Estados honestos por tab.
//
// Se monta en dos lugares (mismo componente, sin duplicar):
//   1. Ruta directa `/app/reportes` (RecognitionAnalyticsView, con RoleGuard — providers y sidebar
//      los aporta <AppLayout/> desde la FASE 3, ver CONTRATO-RUTAS.md).
//   2. Sección Admin "Reportería" (REQ-133) → <RecognitionReports /> dentro del shell de admin
//      (mismo <AppLayout/>, con su propio RoleGuard).
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RoleGuard } from '@/auth/RoleGuard';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useRecognitionCatalog, getFundamentalName } from '@/api/recognition';
import type { RecognitionReportParams } from '@/api/recognition';
import { DEFAULT_FILTERS } from '@/views/AnalyticsView/hooks/useAnalyticsFilters';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { useOuScope } from '@/hooks/useOuScope';
import { PageSpinner } from '@/components/common/PageSpinner';
import { useRecognitionTabFilters } from './useRecognitionTabFilters';
import { FilterTags } from './FilterTags';
import { BloquePlantilla } from './BloquePlantilla';
import { BannersDeContexto } from './BannersDeContexto';
import { BannerHomologacion } from './BannerHomologacion';
import { TiraDeFiltros } from './TiraDeFiltros';
import type { FilterTag } from './FilterTags';
import { DailyTab } from './tabs/DailyTab';
import { ShineTab } from './tabs/ShineTab';
import { HistoryTab } from './tabs/HistoryTab';
import { HeatmapTab } from './tabs/HeatmapTab';
import { NetworkTab } from './tabs/NetworkTab';
import { GenerationalTab } from './tabs/GenerationalTab';
import { BrechaGeneracionalTable } from './tabs/BrechaGeneracionalTable';
import { DemograficoExportButton } from './tabs/DemograficoExportButton';
import { LeadersTab } from './tabs/LeadersTab';
import { ModerationReportTab } from './tabs/ModerationReportTab';
import { CampaignsGeneralTab } from './tabs/CampaignsGeneralTab';
import { HEATMAP_ALLOWED_ROLES } from './reportsShared';
import '@/views/AnalyticsView/AnalyticsView.css';
import './RecognitionReports.css';

/**
 * C2 — las SIETE de la maqueta. `heatmap` y `network` salen del tipo a propósito, y no solo de la
 * barra: dejándolos, una ruta con `?tab=heatmap` seguiría compilando y pintaría una pestaña que la
 * maqueta no tiene. Su contenido no se pierde — vive dentro de Demográfico.
 */
type ReportTab =
  | 'daily' | 'shine' | 'history' | 'generational' | 'leaders'
  | 'moderation' | 'campaigns';

/**
 * D-13 / P12 — las pestañas que NO consumen la barra de filtros.
 *
 * La maqueta no la oculta en ninguna (`switchRepTab` solo togglea paneles), así que esto es una
 * desviación consciente. El motivo de cada una, medido, porque «coherencia técnica» no era motivo:
 *
 *   · `history`    — RF-150: el alcance temporal lo da la CAMPAÑA que se elige en la tabla, no un
 *                    rango. Filtrar por fechas encima no acotaría nada.
 *   · `campaigns`  — sus cifras salen de `useCampaignSummaries`, que consulta cada campaña en SU
 *                    propia vigencia y con el alcance del rol. Ni el rango ni la OU de la barra
 *                    entran en esa consulta, así que la barra se vería y no haría nada.
 *
 * 🔴 La regla que las dos comparten: **un filtro que no filtra es peor que no tener barra.** Si
 * alguna pasa a consumirla, se quita de aquí — no se deja «por si acaso».
 *
 * `moderation` estuvo en esta lista y SALIÓ el 2026-09-04: su endpoint ya acepta OU, rango y
 * persona, así que ocultarle la barra entera dejaba la bandeja de casos sin forma de acotarse. El
 * único filtro que no honra es Fundamental, y eso lo declara el propio panel con
 * `AvisoFiltroNoAplica` — ocultar los cuatro controles por uno que no aplica era demasiado.
 */
const TABS_WITHOUT_FILTER_BAR: ReadonlySet<ReportTab> = new Set<ReportTab>([
  'history',
  'campaigns',
]);

/**
 * C2 — las SIETE pestañas de la maqueta, en su orden (`switchRepTab` en `fundamentales-admin.js`):
 * Reco Diario · Brillamos Juntos · Históricos · Demográfico · Campañas General · Líderes · Moderación.
 *
 * `heatmap` y `network` DEJAN de ser pestañas: en la maqueta su contenido vive dentro del panel
 * Demográfico (el mapa «OU × Fundamental» y la tarjeta de colaboración cross-OU están los dos en
 * `rep-panel-demografico`). No se borra nada — se mueve, que es lo que pide la regla 1:1.
 */
/**
 * Paridad visual Fase 8 (2026-09-11) — íconos calcados de la maqueta (`fund-puntos-tabs`, emoji
 * 🏅⭐📊👥🎯👤🚨), a su equivalente más cercano en FontAwesome (el set que ya usa toda la app; no
 * se introduce una fuente de emoji nueva). `fa-star` ya coincidía; Demográfico/Líderes/Moderación
 * se dejan igual — ya transmiten el mismo concepto (grupo de personas / persona / alerta) y no hay
 * un ícono FA idéntico al emoji.
 */
export const TABS: Array<{ id: ReportTab; icon: string; labelKey: string }> = [
  { id: 'daily',        icon: 'fa-medal',              labelKey: 'reports.tabs.daily'        },
  { id: 'shine',        icon: 'fa-star',               labelKey: 'reports.tabs.shine'        },
  { id: 'history',      icon: 'fa-chart-bar',          labelKey: 'reports.tabs.history'      },
  { id: 'generational', icon: 'fa-people-group',       labelKey: 'reports.tabs.generational' },
  { id: 'campaigns',    icon: 'fa-bullseye',           labelKey: 'reports.tabs.campaigns'   },
  { id: 'leaders',      icon: 'fa-user-tie',           labelKey: 'reports.tabs.leaders'      },
  { id: 'moderation',   icon: 'fa-triangle-exclamation', labelKey: 'reports.tabs.moderation' },
];

/**
 * Contenido de la reportería (FilterBar + tabs). Se reutiliza tal cual dentro del shell de Admin
 * (REQ-133) y bajo la ruta directa. No incluye providers ni guard: los aporta cada contenedor.
 */
export function RecognitionReports() {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const [activeTab, setActiveTab] = useState<ReportTab>('daily');
  // Matriz de permisos de Reconocimiento — "Exportar datos" (RN-K03) es independiente de "Ver
  // reportería": quien entra a esta pantalla (ya autorizado por el RoleGuard de arriba) puede no
  // tener el permiso granular de exportación. `?.` en cascada: mientras el perfil resuelve,
  // `recognitionPermissions` es `null`/`undefined` y el gate falla CERRADO (botones ocultos), nunca
  // abierto por defecto.
  const currentUser = useCurrentUser();
  const canExportData = currentUser?.recognitionPermissions?.includes('exportData') ?? false;
  // D-13 — barra de filtros COMPARTIDA por todas las pestañas: la maqueta la deja fuera de los
  // paneles y no la reinicia al cambiar de tab, así que los filtros se heredan.
  const { filters, setFilter, resetFilters, resetFilter } = useRecognitionTabFilters(activeTab);
  const { resolveOuName } = useOuNameResolver();
  const { isResolving, hasEmptyScope, resolveQueryOuIdsMulti } = useOuScope();

  /** Nombres de las OUs SELECCIONADAS (nunca la lista expandida con descendientes) para tags/banners. */
  const ouNombresSeleccionados = useMemo(
    () => filters.ou.map(resolveOuName).join(', '),
    [filters.ou, resolveOuName],
  );

  /** Lista expandida (selección + descendientes, intersectada con el alcance) para las consultas. */
  const ouIdsExpandidos = useMemo(
    () => resolveQueryOuIdsMulti(filters.ou),
    [filters.ou, resolveQueryOuIdsMulti],
  );

  const { data: catalog } = useRecognitionCatalog();

  /** Lo único que la tira necesita del catálogo: id y nombre ya resuelto al idioma. */
  const fundamentalesDelFiltro = useMemo(
    () => (catalog ?? []).map((f) => ({ id: f.id, nombre: getFundamentalName(f, lang) })),
    [catalog, lang],
  );

  const params = useMemo<RecognitionReportParams>(
    () => ({
      ouIds: ouIdsExpandidos,
      startDate: filters.dateFrom || undefined,
      endDate: filters.dateTo || undefined,
      fundamentalId: filters.fundFilter || undefined,
      personaId: filters.persona?.id,
      language: lang,
    }),
    [ouIdsExpandidos, filters.dateFrom, filters.dateTo, filters.fundFilter, filters.persona, lang],
  );

  // La barra es única y compartida (D-13), pero tres pestañas no la consumen: en ellas se oculta,
  // porque un tag que el panel ignora es peor que no tener barra.
  const showFilterBar = !TABS_WITHOUT_FILTER_BAR.has(activeTab);

  // Tags removibles individuales: uno por filtro activo, cada uno quita solo su propio campo
  // (resetFilter) sin tocar el resto de la barra.
  const filterTags = useMemo<FilterTag[]>(() => {
    const tags: FilterTag[] = [];
    if (filters.ou.length > 0) {
      tags.push({
        key: 'ou',
        label: `${t('reports.filters.ou')}: ${ouNombresSeleccionados}`,
        onRemove: () => resetFilter('ou'),
      });
    }
    if (filters.fundFilter) {
      const fund = catalog?.find((f) => f.id === filters.fundFilter);
      tags.push({
        key: 'fundFilter',
        label: `${t('reports.filters.fundamental')}: ${fund ? getFundamentalName(fund, lang) : filters.fundFilter}`,
        onRemove: () => resetFilter('fundFilter'),
      });
    }
    if (filters.persona) {
      tags.push({
        key: 'persona',
        label: `${t('reports.filters.person')}: ${filters.persona.displayName}`,
        onRemove: () => resetFilter('persona'),
      });
    }
    /*
     * 🔴 El rango SIEMPRE se enseña, aunque sea el de por omisión.
     *
     * Antes solo salía si difería del defecto, y como el defecto es la semana en curso, en la práctica
     * NUNCA aparecía: la pantalla mostraba «Fundamental: X» y «OU: Y» como filtros puestos y callaba el
     * recorte que más manda sobre las cifras. Alguien que mira un total sin ver el rango no sabe de
     * cuándo es ese total.
     *
     * La «×» solo aparece cuando hay algo que quitar. En el valor por omisión no se pinta: quitar un
     * rango no puede dejar el reporte sin fechas —el backend exige los dos extremos juntos— así que
     * «quitar» solo puede significar «volver a la semana en curso», y ofrecer eso cuando ya estás en
     * la semana en curso es un botón que no hace nada.
     */
    const rangoPorOmision =
      filters.dateFrom === DEFAULT_FILTERS.dateFrom && filters.dateTo === DEFAULT_FILTERS.dateTo;

    tags.push({
      key: 'dateRange',
      label: `${t('reports.filters.dateRange')}: ${filters.dateFrom} – ${filters.dateTo}`,
      onRemove: rangoPorOmision
        ? undefined
        : () => {
          resetFilter('dateFrom');
          resetFilter('dateTo');
        },
    });
    return tags;
  }, [
    filters.ou, ouNombresSeleccionados, filters.fundFilter, filters.persona, filters.dateFrom,
    filters.dateTo, catalog, lang, resetFilter, t,
  ]);

  /**
   * Los filtros que Moderación SÍ aplica, con los nombres ya resueltos, para que su Excel los
   * declare. Se derivan de los MISMOS chips que pinta la pantalla —menos Fundamental, el único que
   * ese tab no honra—, así el libro exportado no puede decir algo distinto de lo que se ve arriba.
   */
  const filtrosDeModeracion = useMemo(
    () => filterTags
      .filter((tag) => tag.key !== 'fundFilter')
      .map((tag) => tag.label)
      .join(' · ') || null,
    [filterTags],
  );

  // Mientras el alcance no esté resuelto no se monta ningún tab: cada uno dispara su propia
  // consulta con `params.ouIds` en cuanto se renderiza, y hacerlo antes de tiempo manda una
  // petición sin acotar por OU (el backend igual autoriza por rol — no es una fuga de datos —
  // pero es un viaje de más y un parpadeo de totales sin acotar). Mismo patrón que
  // `AnalyticsView/index.tsx`. Los hooks de arriba ya corrieron todos: este return es seguro
  // frente a las reglas de hooks.
  if (isResolving) return <div className="au-shell rr-shell"><PageSpinner /></div>;

  // AdminOU sin ninguna OU asignada dentro de su alcance: no hay nada que reportar.
  if (hasEmptyScope) {
    return (
      <div className="au-shell rr-shell">
        <p className="au-empty-scope">{t('reports.emptyScope')}</p>
      </div>
    );
  }

  return (
    <div className="au-shell rr-shell">
      <div className="au-tabs-outer">
        <div className="au-tabs-row">
          <div className="au-tabs-group" role="tablist" aria-label={t('reports.title')}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`au-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`fas ${tab.icon}`} aria-hidden="true" />
                <span>{t(tab.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/*
        ORDEN DE LA MAQUETA (`fundamentales-admin.js`, bloque `bodies.reporteria`), y el orden es el
        mensaje: pestañas → leyenda de homologación → tira de filtros → tags → «Mostrando» → plantilla.

        🔴 Nada de esto va ENCIMA de las pestañas. Antes sí, y leído de arriba abajo la pantalla decía
        «estos filtros mandan sobre lo que hay debajo», cuando en realidad Históricos y Campañas los
        ignoran. Con las pestañas primero, los filtros quedan visiblemente subordinados a la activa.
      */}
      {/*
        Paridad visual Fase 8 (2026-09-11) — `.rr-filters-card`: en la maqueta, `.fund-tab-panel` es
        UNA sola tarjeta translúcida que envuelve pestañas + filtros + banners; aquí estos 4 bloques
        flotaban sueltos sobre el fondo degradado de la página, incluido el banner verde de
        homologación. Se acota a este grupo (no a `.au-shell`/`.rr-shell` completo, ni a la plantilla,
        que ya tiene su propia tarjeta) para no reabrir todo el layout de la pantalla.
      */}
      <div className="rr-filters-card">
        <BannerHomologacion />

        {showFilterBar && (
          <>
            {/*
              Tira PROPIA de Reportería, en el orden de la maqueta. Sustituye a `FilterBar`, que sigue
              intacta para el módulo Analytics genérico (D-13): llegar a este orden habría exigido
              reordenar un componente compartido, y eso cambia una pantalla que nadie pidió tocar.
            */}
            <TiraDeFiltros
              filters={filters}
              setFilter={setFilter}
              resetFilters={resetFilters}
              fundamentales={fundamentalesDelFiltro}
            />
            <FilterTags tags={filterTags} />

            <BannersDeContexto
              canExportData={canExportData}
              ouName={filters.ou.length > 0 ? ouNombresSeleccionados : null}
              rango={
                filters.dateFrom && filters.dateTo ? `${filters.dateFrom} – ${filters.dateTo}` : null
              }
              fundamentalName={(() => {
                if (!filters.fundFilter) return null;
                const fund = catalog?.find((f) => f.id === filters.fundFilter);
                return fund ? getFundamentalName(fund, lang) : filters.fundFilter;
              })()}
            />
          </>
        )}
      </div>

      {/*
        La plantilla va DESPUÉS de los filtros, aunque no dependa de ellos: el censo es una foto del
        directorio y solo se acota por OU. Es lo último antes del contenido de la pestaña, igual que en
        la maqueta.

        🔴 Pedido del cliente (2026-09-11): quitar el aviso "el filtro Fundamental no aplica" de este
        bloque — aparecía en Reco Diario/Demográfico/Líderes al elegir un Fundamental y se percibía
        como ruido, no como ayuda. Se retiró el prop `filtrosActivos` y el `motivo="censo"` de
        `AvisoFiltroNoAplica` (ver `BloquePlantilla.tsx`) en vez de solo ocultarlo aquí: el bloque
        nunca lo necesitó en ninguna de las 7 pestañas.
      */}
      <BloquePlantilla ouIds={ouIdsExpandidos} />

      <div className="au-tab-panel active" role="tabpanel">
        {activeTab === 'daily' && (
          <DailyTab params={params} canExportData={canExportData} />
        )}
        {activeTab === 'shine' && <ShineTab params={params} canExportData={canExportData} />}
        {activeTab === 'history' && <HistoryTab canExportData={canExportData} />}
        {/*
          * Demográfico ABSORBE el mapa de calor y la red cross-OU: en la maqueta los tres viven en
          * `rep-panel-demografico`. El orden es el de la maqueta — primero la demografía, luego el
          * mapa «OU × Fundamental», y al final la colaboración entre OUs.
          */}
        {activeTab === 'generational' && (
          <>
            {/* C7 — export consolidado (3 hojas, 1 descarga) del panel completo, antes de sus 4
                bloques: no depende de cuál esté visible ni de scroll para encontrarlo. */}
            <DemograficoExportButton
              params={params}
              analyticsFilters={{ ouIds: ouIdsExpandidos, language: lang }}
              canExportData={canExportData}
            />
            <GenerationalTab params={params} />
            {/* «Brecha Generacional × Fundamental» — hoja del Excel 04, en el mismo panel. */}
            <BrechaGeneracionalTable filters={{ ouIds: ouIdsExpandidos, language: lang }} params={params} />
            {/*
              * C7 (ronda de revisión) — Mapa de Calor exige la capacidad `MapaDeCalor`
              * (`ViewAnalytics` + `PermissionScope.Global`, SIN `RestrictedToRoles` —
              * `CapabilityPolicy.cs`). `AdminGlobal` Y `AdminBrillamos` tienen `MaxScope: Global`
              * (`RoleCatalog.cs`) y reciben el MISMO trato en las 12 secciones de Reconocimiento
              * (`accessMatrix.ts` → `RECOGNITION_DIRECT_ACCESS`, incluida `reporteria`): el backend
              * autoriza a los dos con 200. Solo `AdminOUBrillamos` (`MaxScope: OuBu`) recibe 403
              * confirmado. Lista compartida con `DemograficoExportButton` en
              * `HEATMAP_ALLOWED_ROLES` (`reportsShared.tsx`) — ver ahí el detalle completo y por qué
              * NO se usa `orPermission` para esto.
              *
              * Independiente del RoleGuard de arriba (AdminGlobal/AdminBrillamos/AdminOUBrillamos),
              * que sigue cubriendo a Generacional y Red — esas dos siguen bajo `MapaCalorYRedCrossOu`,
              * que sí admite el acotado por OU/BU.
              */}
            <RoleGuard allow={HEATMAP_ALLOWED_ROLES}>
              <HeatmapTab params={params} />
            </RoleGuard>
            <NetworkTab params={params} />
          </>
        )}
        {activeTab === 'leaders' && <LeadersTab params={params} canExportData={canExportData} />}
        {activeTab === 'moderation' && (
          <ModerationReportTab
            params={params}
            filtrosAplicados={filtrosDeModeracion}
            canExportData={canExportData}
          />
        )}
        {activeTab === 'campaigns' && <CampaignsGeneralTab canExportData={canExportData} />}
      </div>
    </div>
  );
}

// FASE 3 (paridad demo) — antes montaba su propio UserProvider/ThemeProvider/GradientBackground/
// <main> (shell duplicado, sin sidebar). Ahora cuelga de <AppLayout/> como el resto de /app/*.
/** Ruta directa `/app/reportes` — gate por rol admin (analítica/reportería). */
export function RecognitionAnalyticsView() {
  const { t } = useTranslation('recognition');
  return (
    <div className="adm-shell">
      <header className="page-header adm-header">
        <div>
          <h1 className="page-title">{t('reports.title')}</h1>
          <p className="page-subtitle">{t('reports.subtitle')}</p>
        </div>
        <Link to="/app/admin/reporteria" className="adm-back">
          <i className="fas fa-arrow-left" aria-hidden="true" /> {t('admin.title')}
        </Link>
      </header>
      <div className="adm-body">
        <RoleGuard
          allow={['AdminGlobal', 'AdminBrillamos', 'AdminOUBrillamos']}
          fallback={<div className="adm-denied" role="alert"><i className="fas fa-lock" /> {t('admin.denied')}</div>}
        >
          <RecognitionReports />
        </RoleGuard>
      </div>
    </div>
  );
}

export default RecognitionAnalyticsView;
